import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Briefcase,
  Trash2,
  IndianRupee,
  FileText,
  Calendar,
  X,
  ChevronLeft,
  Pencil,
} from "lucide-react";
import FieldOfWork from "components/Reusable/FieldOfWork";
import { ContractorDashboardTab } from "./ContractorDashboardTab";
import { ContractorWorkOrdersTab } from "./ContractorWorkOrdersTab";
import { ContractorPaymentsTab } from "./ContractorPaymentsTab";
import { ContractorAttendanceTab } from "./ContractorAttendanceTab";
import { ContractorApi } from "../../../api/contractors/service";
import EllipsisMenu from "components/UI_components/EllipsisMenu";

// Utility for classes
const cn = (...classes) => classes.filter(Boolean).join(" ");

export function ContractorProfile({
  contractor,
  workOrders,
  payments,
  contractors,
  onBack,
  onAddPayment,
  onOpenCreateWorkOrder,
  onViewWorkOrder,
  onUpdated,
  onDeleted,
  onDeletePayment,
}) {
  const [activeTab, setActiveTab] = useState("workorders");
  const [contractorInfo, setContractorInfo] = useState(contractor);

  const getInitialContact = (contact) => {
    if (!contact) return "";
    return contact.startsWith("+91") ? contact.slice(3) : contact;
  };

  const [editValues, setEditValues] = useState({
    name: contractor?.name || "",
    contact: getInitialContact(contractor?.contact),
    address: contractor?.address || "",
    category: contractor?.category || "",
    workname_id: contractor?.workname_id || null,
    other_workname: contractor?.other_workname || null,
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFieldOfWork, setShowFieldOfWork] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editError, setEditError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const currentContractor = contractorInfo || contractor || {};

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount.toLocaleString()}`;
  };

  useEffect(() => {
    setContractorInfo(contractor);
    setEditValues({
      name: contractor?.name || "",
      contact: getInitialContact(contractor?.contact),
      address: contractor?.address || "",
      category: contractor?.category || "",
      workname_id: contractor?.workname_id || null,
      other_workname: contractor?.other_workname || null,
    });
  }, [contractor]);

  const handleUpdateContractor = async () => {
    setIsSavingEdit(true);
    setEditError("");
    try {
      const payload = {
        name: editValues.name,
        contact:
          editValues.contact?.length === 10
            ? `+91${editValues.contact}`
            : editValues.contact,
        address: editValues.address,
        workname_id: editValues.workname_id,
        other_workname: editValues.other_workname,
      };
      const res = await ContractorApi.updateContractor(contractor.id, payload);
      const data = res?.data || res;
      if (data) {
        const updatedContractor = {
          ...payload,
          category: editValues.category,
        };
        setContractorInfo((prev) => ({ ...prev, ...updatedContractor }));
        onUpdated?.(updatedContractor);
        setEditValues({
          name: payload.name || "",
          contact: getInitialContact(payload.contact),
          address: payload.address || "",
          category: editValues.category || "",
          workname_id: editValues.workname_id || null,
          other_workname: editValues.other_workname || null,
        });
        setEditError("");
        setShowEditModal(false);
      } else {
        setEditError("Failed to update contractor");
      }
    } catch (err) {
      setEditError(err?.message || "Failed to update contractor");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError("");
    try {
      await ContractorApi.deleteById(contractor.id);
      onDeleted?.(contractor.id);
      onBack?.();
    } catch (err) {
      setDeleteError(err?.message || "Failed to delete contractor");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="w-full px-4 text-black mt-8 flex flex-col">
      {/* Header */}
      <div className="border bg-white border-gray-200 rounded-[12px] p-4 mb-3">
        <div className="flex justify-between items-start w-full">
          <div className="flex gap-4 items-start">
            <button
              onClick={onBack}
              className="text-[#232323] hover:text-black mt-1"
              type="button"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E9E3F3] flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-[#7A4DCC]" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-[16px] text-[#232323]">
                    {currentContractor.name || "N/A"}
                  </h1>
                  <span className="text-xs px-2 py-0 bg-[#E3E3E3] text-[#737373] rounded-full font-medium border border-gray-300">
                    Contractor
                  </span>
                </div>
                <p className="text-[14px] text-gray-600 mt-1">
                  {currentContractor.category || "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-sm">
              <p className="text-gray-600">
                <span className="inline-block w-18">Address :</span>
                <span className="ml-1">
                  {currentContractor.address || "N/A"}
                </span>
              </p>
              <p className="text-gray-600">
                <span className="inline-block w-17">Contact No :</span>
                <span className="ml-1">
                  <span className="text-[14px] text-[#232323]">
                    {currentContractor.contact || "N/A"}
                  </span>
                </span>
              </p>
            </div>
            <div className="mt-1">
              <EllipsisMenu
                items={[
                  {
                    key: "edit-contractor",
                    label: "Edit",
                    icon: Pencil,
                    onClick: () => setShowEditModal(true),
                    disabled: isSavingEdit || isDeleting,
                  },
                  { type: "separator" },
                  {
                    key: "delete-contractor",
                    label: "Delete",
                    icon: Trash2,
                    danger: true,
                    onClick: () => setShowDeleteModal(true),
                    disabled: isSavingEdit || isDeleting,
                  },
                ]}
                buttonAriaLabel="Contractor profile actions"
              />
            </div>
          </div>
        </div>

        <hr className="my-3 border-gray-600" />

        {/* <div className="ml-2"> */}
        {/* <h2 className="text-[20px] text-[#232323] mb-3">Payment Info</h2> */}

        <div className="grid grid-cols-4 gap-4 w-full">
          {[
            {
              label: "Total Work Order Value",
              value: formatCurrency(currentContractor.totalWorkOrderValue || 0),
              icon: FileText,
              iconBgClass: "bg-sm-secondary/10",
              iconTextClass: "text-sm-secondary",
            },
            {
              label: "Total Paid",
              value: formatCurrency(currentContractor.totalPaid || 0),
              icon: IndianRupee,
              iconBgClass: "bg-green-500/10",
              iconTextClass: "text-green-500",
            },
            {
              label: "Total Due",
              value: formatCurrency(currentContractor.totalDue || 0),
              icon: IndianRupee,
              iconBgClass: "bg-red-500/10",
              iconTextClass: "text-red-500",
            },
            {
              label: "Active Projects",
              value: currentContractor.activeProjects || 0,
              icon: Calendar,
              iconBgClass: "bg-sm-primary/10",
              iconTextClass: "text-sm-primary",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="rounded-lg border border-border/60 bg-white shadow-none p-3 flex items-center gap-3 min-w-0"
            >
              <div
                className={`h-10 w-10 rounded-lg flex items-center justify-center ${item.iconBgClass}`}
              >
                <item.icon className={`w-5 h-5 ${item.iconTextClass}`} />
              </div>
              <div className="min-w-0">
                <p className="text-[#232323] text-2xl font-semibold whitespace-nowrap leading-tight">
                  {item.value}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {item.label}
                </p>
              </div>
            </div>
          ))}
        </div>
        {/* </div> */}
      </div>

      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          {!showFieldOfWork ? (
            <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <p className="text-base font-semibold text-gray-900">
                  Edit Contractor
                </p>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditValues({
                      name: contractor?.name || "",
                      contact: getInitialContact(contractor?.contact),
                      address: contractor?.address || "",
                      category: contractor?.category || "",
                      workname_id: contractor?.workname_id || null,
                      other_workname: contractor?.other_workname || null,
                    });
                    setEditError("");
                  }}
                  className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="space-y-4 px-6 py-4">
                {editError && (
                  <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 border border-red-100">
                    {editError}
                  </div>
                )}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    type="text"
                    value={editValues.name}
                    onChange={(e) =>
                      setEditValues((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-sm-primary focus:outline-none focus:ring-1 focus:ring-sm-primary"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">
                    Category
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowFieldOfWork(true)}
                    className="w-full flex h-9 items-center justify-between rounded-md border border-gray-300 px-3 py-2 text-sm text-left hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-sm-primary"
                  >
                    <span
                      className={
                        editValues.category ? "text-gray-900" : "text-gray-500"
                      }
                    >
                      {editValues.category || "Select category"}
                    </span>
                  </button>
                </div>
                {[
                  { label: "Contact Number", key: "contact" },
                  { label: "Address", key: "address" },
                ].map((field) => (
                  <div key={field.key} className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">
                      {field.label}
                    </label>
                    {field.key === "contact" ? (
                      <div className="flex items-center border border-gray-300 rounded-md overflow-hidden focus-within:ring-1 focus-within:ring-sm-primary focus-within:border-sm-primary">
                        <span className="px-3 py-2 bg-gray-50 text-sm font-medium text-gray-700 border-r border-gray-300">
                          +91
                        </span>
                        <input
                          type="tel"
                          value={editValues[field.key]}
                          onChange={(e) => {
                            let value = e.target.value;
                            value = value.replace(/\D/g, "").slice(0, 10);
                            setEditValues((prev) => ({
                              ...prev,
                              [field.key]: value,
                            }));
                          }}
                          maxLength={10}
                          placeholder="9999999999"
                          className="flex-1 px-3 py-2 text-sm focus:outline-none bg-white"
                        />
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={editValues[field.key]}
                        onChange={(e) =>
                          setEditValues((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }))
                        }
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-sm-primary focus:outline-none focus:ring-1 focus:ring-sm-primary"
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-3 bg-gray-50">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditValues({
                      name: contractor?.name || "",
                      contact: getInitialContact(contractor?.contact),
                      address: contractor?.address || "",
                      category: contractor?.category || "",
                      workname_id: contractor?.workname_id || null,
                      other_workname: contractor?.other_workname || null,
                    });
                    setEditError("");
                  }}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                  disabled={isSavingEdit}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateContractor}
                  className="rounded-md bg-sm-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-sm-primary/90 transition-colors disabled:opacity-70"
                  disabled={
                    isSavingEdit ||
                    !String(editValues.name || "").trim() ||
                    !String(editValues.contact || "").trim() ||
                    String(editValues.contact || "").length !== 10 ||
                    !editValues.category
                  }
                >
                  {isSavingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          ) : (
            <div className="w-full max-w-md rounded-xl bg-white shadow-xl p-6">
              <button
                type="button"
                onClick={() => setShowFieldOfWork(false)}
                className="flex items-center mb-4 text-sm-primary text-sm font-medium hover:underline gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to edit
              </button>
              <FieldOfWork
                onSelect={(work) => {
                  setEditValues((prev) => ({
                    ...prev,
                    category: work.name,
                    workname_id: work.id,
                    other_workname: work.other_workname,
                  }));
                  setShowFieldOfWork(false);
                }}
              />
            </div>
          )}
        </div>
      )}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="w-full max-w-sm rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <p className="text-base font-semibold text-gray-900">
                Delete Contractor
              </p>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="space-y-3 px-6 py-4 text-sm text-gray-700">
              <p>
                Are you sure you want to delete this contractor? This action
                cannot be undone.
              </p>
              {deleteError && (
                <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 border border-red-100">
                  {deleteError}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-3 bg-gray-50">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-white hover:bg-destructive/90 transition-colors disabled:opacity-70"
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Tabs */}
      <div className="flex-1 flex flex-col mt-2">
        <div className="h-10 p-1 w-fit bg-[#F2F1F3] rounded-lg flex items-center space-x-1">
          {["dashboard", "workorders", "payments", "attendance"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                "h-8 text-sm px-4 rounded-md transition-all font-medium",
                activeTab === tab
                  ? "bg-[#FDFDFD] shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="flex-1 mt-4">
          {activeTab === "dashboard" && (
            <ContractorDashboardTab
              contractor={contractor}
              workOrders={workOrders}
              payments={payments}
            />
          )}
          {activeTab === "workorders" && (
            <ContractorWorkOrdersTab
              workOrders={workOrders}
              contractors={contractors}
              contractor={contractor} // ← ADD THIS
              selectedContractorId={contractor.id}
              onOpenCreateWorkOrder={onOpenCreateWorkOrder}
              onViewWorkOrder={onViewWorkOrder}
              // onDownloadWorkOrder={handleDownloadWorkOrder}
            />
          )}
          {activeTab === "payments" && (
            <ContractorPaymentsTab
              payments={payments}
              workOrders={workOrders}
              contractor={contractor}
              onAddPayment={onAddPayment}
              onDeletePayment={onDeletePayment}
            />
          )}
          {activeTab === "attendance" && (
            <ContractorAttendanceTab
              contractor={contractor}
              workOrders={workOrders}
            />
          )}
        </div>
      </div>
    </div>
  );
}
